import type { Route } from './router'
import { getCategoryBySlug, getProductBySlug } from './data/products'
import { getCategorySeoContent, getProductSeoContent } from './data/seo-content'

export const SITE_NAME = 'Angel Technology'
export const SITE_ORIGIN = 'https://www.angeltechpo.com.cn'
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/images/og-cover.png`

export type PageMeta = {
  title: string
  description: string
  keywords: string[]
  ogImage: string
  path: string
}

export function fullTitle(title: string): string {
  return title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`
}

export function getPageMeta(route: Route): PageMeta {
  if (route.name === 'category') {
    const category = getCategoryBySlug(route.slug)
    if (category) {
      const seoContent = getCategorySeoContent(route.slug)
      return {
        title: seoContent?.metaTitle ?? `${category.category} - Prosthetic Components`,
        description:
          seoContent?.metaDescription ??
          `Browse ${category.items.length} ${category.category.toLowerCase()} products from Angel Technology: prosthetic components and rehabilitation supplies for global distributors and clinics.`,
        keywords: [
          ...(seoContent ? [seoContent.primaryTerm] : []),
          `${category.category} supplier`,
          `${category.category} manufacturer`,
          'prosthetic components'
        ],
        ogImage: DEFAULT_OG_IMAGE,
        path: `/category/${route.slug}`
      }
    }
  }

  if (route.name === 'product') {
    const product = getProductBySlug(route.slug)
    if (product) {
      const seoContent = getProductSeoContent(route.slug)
      return {
        title: `${product.code} ${product.name} Supplier`,
        description: `${product.summary} Wholesale and OEM supply for distributors, prosthetic clinics and rehabilitation centres.`,
        keywords: seoContent?.searchTerms ?? [product.name, product.code, product.category, 'prosthetic components'],
        ogImage: `${SITE_ORIGIN}${product.image}`,
        path: `/product/${route.slug}`
      }
    }
  }

  if (route.name === 'notFound') {
    return {
      title: 'Page not found',
      description: 'The requested page could not be found.',
      keywords: [],
      ogImage: DEFAULT_OG_IMAGE,
      path: '/'
    }
  }

  return {
    title: 'Angel Technology | Prosthetic Components & Rehabilitation Supplier',
    description:
      'Angel Technology International Co., Ltd. supplies prosthetic components, orthotic products and rehabilitation materials for global buyers: knee joints, carbon fiber feet, myoelectric hands, liners and adapters.',
    keywords: [
      'prosthetic components',
      'prosthetic knee joint',
      'carbon fiber foot',
      'myoelectric hand',
      'prosthetic liner',
      'orthotic products',
      'rehabilitation products'
    ],
    ogImage: DEFAULT_OG_IMAGE,
    path: '/'
  }
}
